/**
 * Verhindert Doppel-Klicks auf Aktionen, die auf eine API-Antwort warten:
 * Control disable + Wait-Cursor bis die Promise fertig ist.
 */

const BUSY_CLASS = "sunnysign-api-busy";
let busyDepth = 0;

function setDocumentBusy(on: boolean): void {
  if (typeof document === "undefined") return;
  if (on) {
    busyDepth += 1;
    if (busyDepth === 1) document.documentElement.classList.add(BUSY_CLASS);
  } else {
    busyDepth = Math.max(0, busyDepth - 1);
    if (busyDepth === 0) document.documentElement.classList.remove(BUSY_CLASS);
  }
}

export type ApiBusyLock = { inFlight: boolean };

/** Button, Select, Input o. Ä. mit `disabled`. */
export type ApiBusyControl = HTMLElement & { disabled: boolean };

export type RunWithApiBusyOptions = {
  /** Controls die während des Requests disabled werden. */
  buttons?: Array<ApiBusyControl | null | undefined>;
  /** Shared lock — zweiter Aufruf wird übersprungen, solange der erste läuft. */
  lock?: ApiBusyLock;
  /** Nach dem Freigeben (z. B. Length-UI neu berechnen). */
  afterUnlock?: () => void;
};

/**
 * Führt `work` aus; währenddessen Wait-Cursor und disable auf den Controls.
 * Bei aktivem `lock` → `undefined` (kein zweiter Lauf).
 */
export async function runWithApiBusy<T>(
  options: RunWithApiBusyOptions,
  work: () => Promise<T>
): Promise<T | undefined> {
  if (options.lock?.inFlight) return undefined;
  if (options.lock) options.lock.inFlight = true;

  const controls = (options.buttons ?? []).filter((b): b is ApiBusyControl => !!b);
  const previousDisabled = controls.map((b) => b.disabled);
  for (const el of controls) {
    el.disabled = true;
    el.setAttribute("aria-busy", "true");
  }
  setDocumentBusy(true);

  try {
    return await work();
  } finally {
    setDocumentBusy(false);
    controls.forEach((el, i) => {
      el.removeAttribute("aria-busy");
      el.disabled = previousDisabled[i];
    });
    if (options.lock) options.lock.inFlight = false;
    options.afterUnlock?.();
  }
}
